import {
  format,
  isSameYear,
  fromUnixTime,
  formatDistanceToNowStrict,
  differenceInDays,
} from 'date-fns';
import fr from 'date-fns/locale/fr';

const MINUTE_IN_SECONDS = 60;
const HOUR_IN_SECONDS = MINUTE_IN_SECONDS * 60;
const DAY_IN_SECONDS = HOUR_IN_SECONDS * 24;
const MONTH_IN_SECONDS = DAY_IN_SECONDS * 30;
const YEAR_IN_SECONDS = DAY_IN_SECONDS * 365;

const DATE_FNS_LOCALES = {
  fr,
};

const normalizeLocaleCode = locale =>
  locale?.toString().replace(/_/g, '-').toLowerCase();

const isFrenchLocale = locale => normalizeLocaleCode(locale)?.startsWith('fr');

const currentLocale = () => {
  if (typeof window !== 'undefined') {
    return (
      window.chatwootConfig?.selectedLocale ||
      window.document?.documentElement?.lang ||
      window.navigator?.language
    );
  }

  return undefined;
};

const dateFnsLocaleFor = locale => {
  const normalizedLocale = normalizeLocaleCode(locale || currentLocale());
  if (!normalizedLocale) return undefined;

  return (
    DATE_FNS_LOCALES[normalizedLocale] ||
    DATE_FNS_LOCALES[normalizedLocale.split('-')[0]]
  );
};

const dateFnsFormatOptions = locale => {
  const dateLocale = dateFnsLocaleFor(locale);
  return dateLocale ? { locale: dateLocale } : {};
};

export const formatDistanceToNowLocalized = (date, locale) => {
  const dateLocale = dateFnsLocaleFor(locale);
  return formatDistanceToNowStrict(date, {
    addSuffix: true,
    ...(dateLocale ? { locale: dateLocale } : {}),
  });
};

export const formatDateLocalized = (date, dateFormat, locale) => {
  return format(date, dateFormat, dateFnsFormatOptions(locale));
};

/**
 * Formats a Unix timestamp into a human-readable time format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='h:mm a'] - Desired format of the time.
 * @returns {string} Formatted time string.
 */
export const messageStamp = (time, dateFormat, locale) => {
  const unixTime = fromUnixTime(time);
  return formatDateLocalized(unixTime, dateFormat || 'h:mm a', locale);
};

/**
 * Provides a formatted timestamp, adjusting the format based on the current year.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const messageTimestamp = (time, dateFormat, locale) => {
  const messageTime = fromUnixTime(time);
  const now = new Date();
  const formatOptions = dateFnsFormatOptions(locale);
  const messageDate = format(
    messageTime,
    dateFormat || 'MMM d, yyyy',
    formatOptions
  );
  if (!isSameYear(messageTime, now)) {
    return format(messageTime, 'LLL d y, h:mm a', formatOptions);
  }
  return messageDate;
};

export const messageDateTime = (time, locale) => {
  return formatDateLocalized(fromUnixTime(time), 'PP p', locale);
};

/**
 * Converts a Unix timestamp to a relative time string (e.g., 3 hours ago).
 * @param {number} time - Unix timestamp.
 * @returns {string} Relative time string.
 */
export const dynamicTime = (time, locale) => {
  const unixTime = fromUnixTime(time);
  return formatDistanceToNowLocalized(unixTime, locale);
};

export const shortTimestampFromUnix = (
  time,
  { withAgo = false, now = new Date(), locale } = {}
) => {
  const timestamp = Number(time);
  if (!Number.isFinite(timestamp)) return '';

  const seconds = Math.abs(now.getTime() / 1000 - timestamp);
  const suffix = withAgo ? ' ago' : '';
  const formatShortTime = (value, unit) => {
    if (!isFrenchLocale(locale)) return `${value}${unit}${suffix}`;

    const frenchUnits = {
      m: 'min',
      h: 'h',
      d: 'j',
      mo: 'mois',
      y: value > 1 ? 'ans' : 'an',
    };
    return `${value} ${frenchUnits[unit]}`;
  };

  if (seconds < MINUTE_IN_SECONDS) {
    return isFrenchLocale(locale) ? 'maint.' : 'now';
  }
  if (seconds < HOUR_IN_SECONDS) {
    return formatShortTime(
      Math.max(1, Math.round(seconds / MINUTE_IN_SECONDS)),
      'm'
    );
  }
  if (seconds < DAY_IN_SECONDS) {
    return formatShortTime(
      Math.max(1, Math.round(seconds / HOUR_IN_SECONDS)),
      'h'
    );
  }
  if (seconds < MONTH_IN_SECONDS) {
    return formatShortTime(
      Math.max(1, Math.round(seconds / DAY_IN_SECONDS)),
      'd'
    );
  }
  if (seconds < YEAR_IN_SECONDS) {
    return formatShortTime(
      Math.max(1, Math.round(seconds / MONTH_IN_SECONDS)),
      'mo'
    );
  }

  return formatShortTime(
    Math.max(1, Math.round(seconds / YEAR_IN_SECONDS)),
    'y'
  );
};

/**
 * Formats a Unix timestamp into a specified date format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const dateFormat = (time, df, locale) => {
  const unixTime = fromUnixTime(time);
  return formatDateLocalized(unixTime, df || 'MMM d, yyyy', locale);
};

/**
 * Calculates the difference in days between now and a given timestamp.
 * @param {Date} now - Current date/time.
 * @param {number} timestampInSeconds - Unix timestamp in seconds.
 * @returns {number} Number of days difference.
 */
export const getDayDifferenceFromNow = (now, timestampInSeconds) => {
  const date = new Date(timestampInSeconds * 1000);
  return differenceInDays(now, date);
};

/**
 * Checks if more than 24 hours have passed since a given timestamp.
 * Useful for determining if retry/refresh actions should be disabled.
 * @param {number} timestamp - Unix timestamp.
 * @returns {boolean} True if more than 24 hours have passed.
 */
export const hasOneDayPassed = timestamp => {
  if (!timestamp) return true; // Defensive check
  return getDayDifferenceFromNow(new Date(), timestamp) >= 1;
};
